import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import FlowNode from "./FlowNode";
import FlowEdge from "./FlowEdge";
import {
  screenToCanvas, viewBoxOf, edgePath, portPoint, findDropTarget, nodeBox, GRID,
  rectDeDosPuntos, aristaBajoPunto, puedeIntercalarse, nodoSuelto,
} from "./flowGeometry";
import { EDGE_CLR, EDGE_CLR_EST } from "../../constants";
import { accionDe } from "./atajos";
import { estadoProyecto } from "../../utils";

// Px de pantalla que hay que recorrer antes de que pulsar pase a ser arrastrar.
//
// Eran 3 para todo el mundo y se quedaba corto: al pulsar, la mano se mueve. Con
// un raton sensible o un trackpad —donde el dedo empuja el boton y de paso lo
// desplaza— es facil pasar de 3 px sin haber querido arrastrar nada, y entonces
// seleccionar un paso lo movia de sitio y ensuciaba el proyecto.
//
// El dedo necesita bastante mas que el raton: no hay boton que pulsar, asi que
// el contacto entero deriva mientras se apoya, y un umbral de raton en tactil
// convierte casi cualquier toque en un arrastre.
const UMBRAL_RATON  = 6;
const UMBRAL_TACTIL = 14;

export const umbralArrastre = (tipo) => (tipo === "touch" || tipo === "pen"
  ? UMBRAL_TACTIL : UMBRAL_RATON);

// El lienzo. Es el unico sitio con logica de interaccion: los nodos y las
// aristas son componentes puros que solo emiten eventos.
export default function FlowCanvas({
  st, dispatch, editable, minutosPorTarea = {}, atajos = {}, tapadoIzq = 0,
  onCrearArista, onBorrarArista, onIntercalar, onBorrarPaso, onBorrarPasos, onAñadir, onOrganizar,
  onDeshacer, onRehacer, onCursor, onRect, onConectar,
  onAbrirPaso, resaltados, pausado = false,
}) {
  const svgRef = useRef(null);
  const [rect, setRect] = useState({ left: 0, top: 0, width: 800, height: 600 });
  const [espacio, setEspacio] = useState(false);
  // Arista bajo el cursor: es lo que saca su boton de borrar.
  const [aristaEncima, setAristaEncima] = useState(null);
  const punteros = useRef(new Map());     // pinza tactil
  const raf = useRef(0);
  const pinza = useRef(null);

  // El ancho no se puede sacar de useViewport: el panel deslizante y el rail
  // del sidebar cambian el tamaño del contenedor sin cambiar el de la ventana.
  useLayoutEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const medir = () => {
      const r = el.getBoundingClientRect();
      const caja = { left: r.left, top: r.top, width: r.width, height: r.height };
      setRect(caja);
      // Se informa hacia arriba: FlowView necesita la caja para colocar nodos
      // nuevos y para encajar la vista, y medirla por su cuenta se quedaba
      // desfasado en cuanto se abria el panel o cambiaba el tamaño.
      onRect?.(caja);
    };
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    window.addEventListener("scroll", medir, true);
    return () => { ro.disconnect(); window.removeEventListener("scroll", medir, true); };
  }, [onRect]);

  // Al abrirse el panel, lo seleccionado puede quedar DEBAJO.
  //
  // El panel flota sobre el SVG y no lo encoge, asi que el lienzo no se entera
  // de que su franja izquierda ha dejado de verse: pulsabas un paso de la
  // izquierda, se abria el panel encima, y el paso desaparecia justo cuando
  // acababas de elegirlo. Aqui se desplaza la vista lo justo para sacarlo, con
  // un margen para que no quede pegado al borde del panel.
  //
  // Se mueve la VISTA, nunca el nodo: lo que cambia es desde donde se mira.
  //
  // Nunca durante un arrastre: mover la vista a mitad de gesto haria saltar al
  // nodo bajo el cursor.
  const MARGEN_PANEL = 24;
  useEffect(() => {
    if (!tapadoIzq || st.drag) return;
    const ids = st.sel.nodes.length ? st.sel.nodes
      : (st.sel.edge && st.edges[st.sel.edge]
        ? [String(st.edges[st.sel.edge].from_task_id), String(st.edges[st.sel.edge].to_task_id)]
        : []);
    let izquierda = Infinity;
    for (const id of ids) {
      const n = st.nodes[id];
      if (n) izquierda = Math.min(izquierda, nodeBox(n).x);
    }
    if (!Number.isFinite(izquierda)) return;

    // Borde del hueco visible, en coordenadas de lienzo.
    const libre = st.view.x + (tapadoIzq + MARGEN_PANEL) / st.view.k;
    if (izquierda >= libre) return;
    dispatch({ type: "PAN", dxPx: (izquierda - libre) * st.view.k });
    // Solo cuando cambia QUE esta seleccionado o si hay panel: si dependiera de
    // la vista, cada pan volveria a dispararlo.
  }, [st.sel.nodes, st.sel.edge, tapadoIzq]);   // eslint-disable-line

  const aLienzo = useCallback(
    (e) => screenToCanvas(e.clientX, e.clientY, rect, st.view),
    [rect, st.view]
  );

  // ── Rueda ──────────────────────────────────────────────────────────────────
  // onWheel de React se registra como PASIVO, asi que preventDefault() no surte
  // efecto y la rueda haria zoom Y ADEMAS desplazaria la pagina. Hay que
  // suscribirse a mano con passive:false.
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      // Exponencial para que el zoom sea perceptualmente uniforme; lineal se
      // atasca al acercarse al minimo.
      const factor = Math.exp(-e.deltaY * 0.0015);
      dispatch({ type: "ZOOM", cx: e.clientX, cy: e.clientY, factor, rect });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [dispatch, rect]);

  // Si hay una flecha en el aire. Se saca a un booleano porque el efecto de
  // teclado lo necesita en sus dependencias, y con `st.drag` entero el efecto
  // volveria a registrar el listener en cada frame del arrastre: `drag` cambia
  // con cada DRAG_MOVE. Asi solo cambia al empezar y al terminar de enlazar.
  const enlazando = st.drag?.mode === "link";

  // ── Teclado ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const onKeyDown = (e) => {
      // Con una confirmacion abierta, ningun atajo dispara. El velo del dialogo
      // ya bloquea el raton, pero el teclado escucha en window: sin esta
      // puerta, pulsar Supr con el dialogo de "¿eliminar?" delante abria OTRO
      // dialogo detras, y una letra creaba un nodo a espaldas de la pregunta.
      if (pausado) return;

      // No robar teclas a los campos del panel de propiedades
      const a = document.activeElement;
      const escribiendo = a && (a.tagName === "INPUT" || a.tagName === "TEXTAREA"
        || a.tagName === "SELECT" || a.isContentEditable);
      if (escribiendo) return;

      if (e.code === "Space") { setEspacio(true); e.preventDefault(); return; }
      if (e.key === "Escape") { dispatch({ type: "DRAG_CANCEL" }); dispatch({ type: "CLEAR_SEL" }); return; }

      const ctrl = e.ctrlKey || e.metaKey;
      if (ctrl && e.key.toLowerCase() === "z") {
        e.preventDefault();
        // Deshacer puede tener que hablar con el servidor (borrar lo que se
        // acaba de crear, recrear la arista borrada), asi que lo lleva el hook
        // y no un dispatch.
        if (e.shiftKey) onRehacer?.(); else onDeshacer?.();
        return;
      }
      if (ctrl && e.key === "0") { e.preventDefault(); dispatch({ type: "FIT", rect, tapadoIzq }); return; }
      if (!ctrl && (e.key === "+" || e.key === "=")) {
        dispatch({ type: "ZOOM_CENTRO", factor: 1.2, rect }); return;
      }
      if (!ctrl && e.key === "-") {
        dispatch({ type: "ZOOM_CENTRO", factor: 1 / 1.2, rect }); return;
      }

      // Atajos configurables. Van despues de los fijos para que nadie pueda
      // dejarse el editor sin deshacer ni Escape.
      const accion = accionDe(atajos, e);
      if (accion) {
        if (accion.startsWith("nodo:")) {
          if (!editable) return;
          e.preventDefault();
          onAñadir?.(accion.slice(5), true);   // true = donde esta el cursor
          return;
        }
        if (accion.startsWith("conectar:")) {
          if (!editable) return;
          e.preventDefault();
          // Si ya hay una flecha en el aire, el atajo NO arma otra: cambia la
          // que se esta dibujando. Es lo que se espera al pulsar "opcional" a
          // media conexion —quiero que ESTA sea opcional—, y armar una nueva
          // desde el mismo puerto no haria nada visible salvo perder el trazo.
          // Se puede ir y volver todas las veces que haga falta antes de
          // soltarla.
          if (enlazando) {
            dispatch({ type: "DRAG_OPCIONAL", opcional: accion === "conectar:opcional" });
            return;
          }
          onConectar?.(accion === "conectar:opcional");
          return;
        }
        if (accion === "encajar")   { e.preventDefault(); dispatch({ type: "FIT", rect, tapadoIzq }); return; }
        if (accion === "organizar") { if (editable) { e.preventDefault(); onOrganizar?.(); } return; }
        if (accion === "borrar") {
          if (!editable) return;
          e.preventDefault();
          // Se borra lo que este seleccionado, sea una arista o un nodo. La
          // arista va sin preguntar (se rehace tirando de un puerto); un nodo
          // se lleva sus conexiones por delante, asi que ese si avisa.
          if (st.sel.edge) { onBorrarArista?.({ id: st.sel.edge }); return; }
          // En bloque: se borra TODO lo seleccionado, con un solo aviso. Borrar
          // solo el primero de doce era peor que no hacer nada, porque parecia
          // que habia funcionado.
          const nodos = st.sel.nodes.map(x => st.nodes[x]).filter(Boolean);
          if (nodos.length) onBorrarPasos?.(nodos);
          return;
        }
      }

      // Las flechas mueven el LIENZO, que es la otra mitad de haberle quitado
      // el arrastre al boton izquierdo: si el izquierdo barre, moverse por el
      // diagrama tiene que poder hacerse sin raton.
      //
      // Con Shift mueven la seleccion, que es lo que hacian antes. Se cede la
      // tecla desnuda al desplazamiento porque moverse es lo que se hace todo
      // el rato y colocar un nodo al pixel es ocasional; y el ajuste fino de
      // 1 px se pide ahora con Alt.
      if (e.key.startsWith("Arrow") && !e.shiftKey) {
        e.preventDefault();
        const paso = e.altKey ? 40 : 120;   // en pixeles de pantalla
        dispatch({ type: "PAN",
          dxPx: e.key === "ArrowLeft" ? -paso : e.key === "ArrowRight" ? paso : 0,
          dyPx: e.key === "ArrowUp"   ? -paso : e.key === "ArrowDown"  ? paso : 0 });
        return;
      }

      // Mover la seleccion: Shift+flechas, un paso de rejilla, o 1 px con Alt.
      // Se mueve TODO lo seleccionado, que es la unica forma de moverlo: un
      // bloque no se descompone.
      if (editable && st.sel.nodes.length && e.key.startsWith("Arrow")) {
        e.preventDefault();
        const d = e.altKey ? 1 : GRID;
        const dx = e.key === "ArrowLeft" ? -d : e.key === "ArrowRight" ? d : 0;
        const dy = e.key === "ArrowUp"   ? -d : e.key === "ArrowDown"  ? d : 0;
        const desde = {}, grab = {};
        for (const id of st.sel.nodes) {
          const n = st.nodes[id];
          desde[id] = { pos_x: n.pos_x, pos_y: n.pos_y };
          grab[id] = { dx: -dx, dy: -dy };
        }
        // armado: true porque mover con las flechas SI es un movimiento
        // deliberado, y DRAG_END descarta los arrastres sin armar.
        dispatch({ type: "DRAG_START", drag: { mode: "node", ids: st.sel.nodes, grab, desde, armado: true } });
        dispatch({ type: "DRAG_MOVE", p: { x: 0, y: 0 } });
        dispatch({ type: "DRAG_END" });
      }
    };
    const onKeyUp = (e) => { if (e.code === "Space") setEspacio(false); };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, [dispatch, rect, tapadoIzq, editable, atajos, st.sel.nodes, st.sel.edge, st.nodes,
      enlazando, pausado,
      onAñadir, onOrganizar, onBorrarArista, onBorrarPaso, onBorrarPasos,
      onDeshacer, onRehacer, onConectar]);

  // ── Puntero ────────────────────────────────────────────────────────────────
  // El puntero se captura SIEMPRE en el <svg>, nunca en el elemento pulsado.
  //
  // Capturarlo en el <g> del nodo parecia lo natural y era el fallo por el que
  // un paso se quedaba pegado al cursor despues de soltar el boton:
  //
  //   1. pulsas un nodo y su <g> se queda la captura;
  //   2. SEL_NODE lo manda al final del orden de pintado —en SVG no hay z-index,
  //      traer al frente es reordenar el documento—;
  //   3. React MUEVE ese <g> en el DOM, y mover un elemento suelta su captura;
  //   4. el pointerup ya no va al elemento capturado sino a lo que haya debajo
  //      del cursor. Si es el panel que se acaba de abrir —que esta FUERA del
  //      svg— el lienzo no se entera de que has soltado, el arrastre sigue vivo,
  //      y el nodo persigue al raton con el boton levantado.
  //
  // El <svg> no se mueve nunca, asi que su captura aguanta todo el gesto.
  const capturar = (e) => {
    try { svgRef.current?.setPointerCapture(e.pointerId); } catch { /* nada */ }
  };

  // Fondo: pan o deseleccionar. El handler vive en un <rect> que cubre el
  // viewBox y NO en el <svg>: un <g> de SVG no tiene area propia, asi que sin
  // ese rect pulsar "entre" nodos no dispararia nada.
  // El fondo del lienzo. Aqui se reparten los dos gestos que antes eran uno:
  //
  //   boton izquierdo  ->  MARQUESINA (seleccionar varios)
  //   ruleta / espacio ->  arrastrar el lienzo
  //
  // El dedo se queda arrastrando el lienzo, no barriendo: en tactil no hay
  // ruleta ni espacio, asi que si el dedo barriera no habria forma de moverse
  // por el diagrama. Y en movil el editor esta en modo visor de todas formas.
  const fondoDown = (e) => {
    if (e.button === 2) return;
    // Con la flecha armada, pulsar en vacio la cancela. Empezar cualquier otro
    // gesto en ese momento seria lo contrario de lo que se espera.
    if (st.drag?.mode === "link" && st.drag.porTeclado) {
      dispatch({ type: "DRAG_CANCEL" });
      return;
    }
    punteros.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    capturar(e);

    const arrastrarLienzo = e.button === 1 || espacio || e.pointerType !== "mouse";
    if (arrastrarLienzo) {
      // El boton central hace desplazamiento automatico en varios navegadores;
      // sin preventDefault sale el cursor de las cuatro flechas y el lienzo se
      // mueve dos veces.
      if (e.button === 1) e.preventDefault();
      dispatch({
        type: "DRAG_START",
        drag: { mode: "pan", sx: e.clientX, sy: e.clientY, view0: { x: st.view.x, y: st.view.y } },
      });
      return;
    }

    // Marquesina. La seleccion NO se limpia aqui: se resuelve al soltar, donde
    // un rectangulo diminuto —un clic en el vacio— la limpia igual. Limpiarla
    // ya en el pointerdown hacia parpadear la seleccion en cada barrido.
    const p = aLienzo(e);
    dispatch({
      type: "DRAG_START",
      drag: { mode: "marquesina", desdeP: p, hasta: p, sumar: e.shiftKey || e.ctrlKey || e.metaKey },
    });
  };

  // Nodo: stopPropagation para que el fondo no reciba el impacto, y el umbral de
  // arriba antes de mover de verdad. Sin el umbral, un clic seco desplaza el
  // nodo un pixel, que es el defecto mas molesto de estos editores.
  const nodoDown = (e, n) => {
    if (e.button === 2) return;
    e.stopPropagation();

    // Flecha armada por teclado: este clic elige el DESTINO, no selecciona ni
    // arrastra. Se resuelve en el pointerdown y no en el pointerup porque no hay
    // arrastre que esperar: es un clic sobre el destino.
    if (st.drag?.mode === "link" && st.drag.porTeclado) {
      const destino = String(n.id);
      const from = st.drag.from;
      const opcional = !!st.drag.opcional;
      dispatch({ type: "DRAG_CANCEL" });
      if (destino !== from.nodeId) {
        // Sin etiqueta. Escribir "Si" aqui guardaba en la base de datos una
        // copia de lo que ya dice `from_handle`, y esa copia es la que seguia
        // rotulando las flechas. La etiqueta se queda para lo que una persona
        // quiera escribir.
        onCrearArista?.({
          from_task_id: from.nodeId, to_task_id: destino,
          from_handle: from.port, to_handle: "in",
          opcional,
        });
      }
      return;
    }

    capturar(e);
    const id = String(n.id);
    const sumar = e.shiftKey || e.ctrlKey || e.metaKey;
    // Pulsar un nodo que YA forma parte de una seleccion multiple no la deshace:
    // es lo que permite agarrar el bloque por cualquiera de sus nodos y moverlo
    // entero. Sin esta puerta, el SEL_NODE de abajo reducia la seleccion a uno
    // y el bloque se perdia en el primer clic.
    const enBloque = st.sel.nodes.length > 1 && st.sel.nodes.includes(id);
    if (sumar || !enBloque) dispatch({ type: "SEL_NODE", id, sumar });
    if (!editable || espacio) return;

    const p = aLienzo(e);
    const ids = st.sel.nodes.includes(id) && st.sel.nodes.length > 1 ? st.sel.nodes : [id];
    const grab = {}, desde = {};
    for (const i of ids) {
      const b = nodeBox(st.nodes[i]);
      grab[i]  = { dx: p.x - b.x, dy: p.y - b.y };
      desde[i] = { pos_x: st.nodes[i].pos_x, pos_y: st.nodes[i].pos_y };
    }
    dispatch({
      type: "DRAG_START",
      drag: { mode: "node", ids, grab, desde, sx: e.clientX, sy: e.clientY,
        tipo: e.pointerType, armado: false },
    });
  };

  const puertoDown = (e, n, port) => {
    if (!editable || e.button === 2) return;
    e.stopPropagation();
    capturar(e);
    const p = aLienzo(e);
    dispatch({
      type: "DRAG_START",
      drag: { mode: "link", from: { nodeId: String(n.id), port }, to: p, candidato: null },
    });
  };

  // Que arista esta tapando el nodo que se arrastra. Se mira el CENTRO del nodo
  // y no el puntero: el puntero esta donde se agarro —una esquina, el titulo— y
  // usarlo obligaria a apuntar con esa esquina a la linea, que no es lo que se
  // ve. Lo que se ve es el nodo cruzando por encima.
  //
  // Solo con UN nodo: metiendo un bloque entero en una flecha no esta claro cual
  // de ellos hereda la entrada y cual la salida, asi que no se ofrece.
  //
  // Y solo con un nodo SUELTO. Es el gesto que resuelve el caso real: has creado
  // un paso, esta flotando al lado del diagrama, y lo quieres meter en el
  // camino. Con uno ya conectado, arrastrar significa colocarlo, y el resalte
  // seria una trampa que le añadiria dos flechas mas a las que ya tiene.
  const aristaDebajo = (p) => {
    if (!editable) return null;
    const ids = st.drag?.ids || [];
    if (ids.length !== 1) return null;
    const n = st.nodes[ids[0]];
    if (!n || !puedeIntercalarse(n) || !nodoSuelto(ids[0], st.edges)) return null;
    const g = st.drag.grab[ids[0]];
    const b = nodeBox(n);
    // Donde va a quedar el centro con este movimiento, no donde esta ahora: el
    // nodo aun no se ha movido cuando se decide esto.
    const centro = { x: p.x - g.dx + b.w / 2, y: p.y - g.dy + b.h / 2 };
    return aristaBajoPunto(st.edges, st.nodes, centro, new Set(ids));
  };

  const onPointerMove = (e) => {
    // Donde esta el puntero, en coordenadas de lienzo. Lo consume el atajo de
    // insertar para que el nodo nazca aqui. Se escribe en un ref, asi que no
    // provoca ningun re-render.
    onCursor?.(screenToCanvas(e.clientX, e.clientY, rect, st.view));

    if (punteros.current.has(e.pointerId))
      punteros.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    // Pinza tactil: dos punteros a la vez mandan sobre cualquier arrastre
    if (punteros.current.size === 2) {
      const [a, b] = [...punteros.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const centro = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      if (pinza.current) {
        const factor = dist / pinza.current.dist;
        if (Math.abs(factor - 1) > 0.01)
          dispatch({ type: "ZOOM", cx: centro.x, cy: centro.y, factor, rect });
      }
      pinza.current = { dist, centro };
      return;
    }

    if (!st.drag) return;

    // Cinturon y tirantes: si llega un movimiento de raton SIN ningun boton
    // pulsado y hay un arrastre vivo, es que el pointerup se ha perdido por el
    // camino. Se cierra el gesto aqui en vez de dejar el nodo pegado al cursor.
    //
    // No aplica a la flecha armada por teclado, que existe precisamente sin
    // boton pulsado y se cierra con el clic en el destino.
    // finArrastre se declara mas abajo: no importa, porque esto no corre al
    // definir el handler sino al recibir un evento, cuando ya existe.
    if (e.pointerType === "mouse" && e.buttons === 0 && !st.drag.porTeclado) {
      finArrastre(e);
      return;
    }

    // Coalescer con requestAnimationFrame: a 120 Hz y con varios nodos, un
    // dispatch por evento hunde el render. Mismo patron que useViewport.
    cancelAnimationFrame(raf.current);
    const { clientX, clientY } = e;
    raf.current = requestAnimationFrame(() => {
      if (st.drag.mode === "pan") {
        dispatch({ type: "DRAG_MOVE", dxPx: clientX - st.drag.sx, dyPx: clientY - st.drag.sy });
        return;
      }
      const p = screenToCanvas(clientX, clientY, rect, st.view);
      if (st.drag.mode === "marquesina") {
        dispatch({ type: "DRAG_MOVE", p });
        return;
      }
      if (st.drag.mode === "node") {
        if (!st.drag.armado) {
          const d = Math.hypot(clientX - st.drag.sx, clientY - st.drag.sy);
          if (d <= umbralArrastre(st.drag.tipo)) return;
          dispatch({ type: "DRAG_START", drag: { ...st.drag, armado: true } });
        }
        dispatch({ type: "DRAG_MOVE", p, intercalar: aristaDebajo(p) });
        return;
      }
      if (st.drag.mode === "link") {
        const cand = findDropTarget(st.nodes, p, st.drag.from.nodeId, st.order);
        dispatch({ type: "DRAG_MOVE", p, candidato: cand });
      }
    });
  };

  const finArrastre = async (e) => {
    punteros.current.delete(e.pointerId);
    if (punteros.current.size < 2) pinza.current = null;
    cancelAnimationFrame(raf.current);
    if (!st.drag) return;

    if (st.drag.mode === "link") {
      // La armada por teclado la cierra el clic en el destino (ver nodoDown),
      // no el soltar del boton: si se cerrara aqui, el propio clic que la
      // dispara la resolveria contra lo que hubiera debajo.
      if (st.drag.porTeclado) return;
      const destino = st.drag.candidato;
      const from = st.drag.from;
      dispatch({ type: "DRAG_CANCEL" });
      if (destino && destino.nodeId !== from.nodeId) {
        // Sin etiqueta, por lo mismo que en el otro camino de creacion: era
        // una copia de `from_handle` y es la que seguia rotulando la flecha.
        await onCrearArista?.({
          from_task_id: from.nodeId,
          to_task_id:   destino.nodeId,
          from_handle:  from.port,
          to_handle:    "in",
        });
      }
      return;
    }
    // Soltar sobre una arista resaltada: el nodo se mete en medio del camino.
    // Se lee ANTES del DRAG_END, que es quien limpia el arrastre, y se deja que
    // el DRAG_END haga su trabajo de siempre —ajustar a la rejilla y anotar el
    // movimiento— porque la posicion en la que se ha soltado es la buena.
    const aIntercalar = st.drag.mode === "node" && st.drag.armado && st.drag.intercalar
      ? { arista: st.edges[String(st.drag.intercalar)], nodo: st.nodes[st.drag.ids[0]] }
      : null;

    dispatch({ type: "DRAG_END" });

    if (aIntercalar?.arista && aIntercalar.nodo) {
      await onIntercalar?.(aIntercalar);
    }
  };

  // ── Pintado ────────────────────────────────────────────────────────────────
  const ciclos = new Set(st.cycles);

  // El estado de TODO el proyecto, en un color. Lo lleva el nodo de inicio y
  // nadie mas: es la puerta del flujo y el sitio donde se mira primero.
  //
  // Se probo a teñir cada flecha con el estado del paso del que salia, y se
  // quito: bastaba terminar el primer paso para que medio diagrama fuera verde.
  const resumen = estadoProyecto(Object.values(st.nodes));
  const resalte = resaltados && resaltados.length ? new Set(resaltados.map(String)) : null;

  // Previsualizacion de la arista que se esta creando.
  //
  // Sale ya del color de su rama, no del color de "arista nueva": si tiras del
  // circulito verde, lo que sigue al cursor es verde desde el primer pixel, y
  // al soltar la arista se queda igual. El color no aparece al terminar como
  // una sorpresa. Y manda sobre el violeta de opcional, que lo dice el trazo.
  let preview = null;
  let previewClase = st.drag?.opcional ? "opt" : "sel";
  if (st.drag?.mode === "link") {
    const nodoOrigen = st.nodes[st.drag.from.nodeId];
    if (nodoOrigen) {
      const a = portPoint(nodoOrigen, st.drag.from.port);
      const destino = st.drag.candidato ? st.nodes[st.drag.candidato.nodeId] : null;
      const b = destino
        ? portPoint(destino, "in")
        : { ...st.drag.to, dir: [-1, 0] };
      preview = edgePath(a, b).d;
      if (st.drag.from.port === "si" || st.drag.from.port === "no") {
        previewClase = st.drag.from.port;
      }
    }
  }

  const clases = ["df-svg"];
  if (espacio || st.drag?.mode === "pan") clases.push(st.drag?.mode === "pan" ? "df-panning" : "df-pan");
  if (st.drag?.mode === "link") clases.push("df-linking");

  // El rect de fondo cubre el viewBox actual, con holgura para que el pan no
  // deje esquinas muertas.
  const vb = {
    x: st.view.x - rect.width / st.view.k,
    y: st.view.y - rect.height / st.view.k,
    w: rect.width * 3 / st.view.k,
    h: rect.height * 3 / st.view.k,
  };

  return (
    <svg ref={svgRef} className={clases.join(" ")} tabIndex={0}
      role="application" aria-label="Diagrama de flujo del proyecto"
      viewBox={viewBoxOf(st.view, rect)}
      onPointerDown={fondoDown}
      onPointerMove={onPointerMove}
      onPointerUp={finArrastre}
      onPointerCancel={finArrastre}
      onPointerLeave={() => onCursor?.(null)}
      onContextMenu={e => e.preventDefault()}>

      <defs>
        {/* Un marker POR COLOR. fill="context-stroke" no existe en Safari < 16 y
            la flecha saldria negra. */}
        {Object.entries({ ...EDGE_CLR, ...EDGE_CLR_EST }).map(([k, c]) => (
          <marker key={k} id={`df-arrow-${k}`} viewBox="0 0 10 10" refX="9" refY="5"
            markerWidth="6" markerHeight="6" orient="auto-start-reverse"
            markerUnits="strokeWidth">
            <path d="M0,0 L10,5 L0,10 Z" fill={c} />
          </marker>
        ))}
        {/* Rejilla en unidades de lienzo: al escalar el viewBox hace lo correcto
            sin trucos. */}
        <pattern id="df-grid" width={GRID * 4} height={GRID * 4} patternUnits="userSpaceOnUse">
          <circle className="df-grid-dot" cx={0.5} cy={0.5} r={0.9} />
        </pattern>
      </defs>

      {/* Capa 0: fondo. Recibe el pan y el clic para deseleccionar. */}
      <rect className="df-bg" x={vb.x} y={vb.y} width={vb.w} height={vb.h}
        fill="url(#df-grid)" />

      {/* Capa 1: aristas, debajo de los nodos */}
      {Object.values(st.edges).map(e => (
        <FlowEdge key={e.id} e={e} nodos={st.nodes} editable={editable}
          seleccionada={String(st.sel.edge) === String(e.id)}
          ciclo={ciclos.has(String(e.id))}
          atenuada={!!resalte && !(resalte.has(String(e.from_task_id)) && resalte.has(String(e.to_task_id)))}
          encima={String(aristaEncima) === String(e.id)}
          intercalar={String(st.drag?.intercalar ?? "") === String(e.id)}
          /* Solo de un paso de trabajo: un nodo de control no tiene estado
             propio —el suyo se queda en pendiente para siempre— y su color ya es
             el gris que le corresponde, asi que la flecha que le apunta acaba
             gris tambien. La regla se cumple sola. */
          estadoDestino={st.nodes[String(e.to_task_id)]?.kind === "tarea"
            ? st.nodes[String(e.to_task_id)]?.status : null}
          onEntrar={ed => setAristaEncima(String(ed.id))}
          onSalir={ed => setAristaEncima(p => String(p) === String(ed.id) ? null : p)}
          onSeleccionar={ed => dispatch({ type: "SEL_EDGE", id: ed.id })}
          onBorrar={ed => { setAristaEncima(null); onBorrarArista?.(ed); }} />
      ))}

      {/* La marquesina. Se pinta por encima de las aristas y por debajo de los
          nodos, para que no tape lo que se esta seleccionando: relleno muy
          tenue y trazo guionado, porque lo que hay debajo es justo lo que se
          esta eligiendo. El trazo lleva vectorEffect para seguir midiendo 1 px
          de pantalla a cualquier zoom: es una herramienta, no parte del dibujo.
          El estilo va en linea y no en una clase df-* porque constants.js lo
          tiene tocado la otra sesion, y un <rect> sin su CSS no sale
          invisible: sale negro y relleno, tapando el diagrama. */}
      {st.drag?.mode === "marquesina" && st.drag.hasta && (() => {
        const r = rectDeDosPuntos(st.drag.desdeP, st.drag.hasta);
        return (
          <rect x={r.x} y={r.y} width={r.w} height={r.h}
            fill="var(--accent)" fillOpacity={0.08}
            stroke="var(--accent)" strokeWidth={1} strokeDasharray="4 3"
            vectorEffect="non-scaling-stroke" pointerEvents="none" />
        );
      })()}

      {/* Capa 2: la arista en construccion, por encima de las demas */}
      {preview && (
        // Translucida MIENTRAS se dibuja, y opaca en cuanto se conecta. La
        // opacidad dice el estado: lo que se ve es una intencion, todavia no
        // una conexion, y al soltar el cambio a solido es el acuse de que ha
        // quedado hecha. Antes solo cambiaba el guionado, que se nota mucho
        // menos.
        //
        // Va en opacity y no en un color con alfa para que valga igual con
        // cualquiera de los colores de rama —y para que la punta de flecha, que
        // sale de un <marker> con su propio relleno, se atenue con la linea; con
        // un color transparente se habria quedado opaca ella sola.
        <path d={preview} fill="none" opacity={0.45}
          stroke={EDGE_CLR[previewClase]} strokeWidth={2}
          strokeDasharray={st.drag?.opcional ? "7 5" : "5 4"}
          markerEnd={`url(#df-arrow-${previewClase})`}
          pointerEvents="none" />
      )}

      {/* Capa 3: nodos, en su orden de pintado */}
      {st.order.map(id => {
        const n = st.nodes[id];
        if (!n) return null;
        const candidato = st.drag?.mode === "link" && st.drag.candidato?.nodeId === id;
        return (
          <g key={id}>
            {candidato && (() => {
              const b = nodeBox(n);
              return (
                <rect x={b.x - 6} y={b.y - 6} width={b.w + 12} height={b.h + 12} rx={12}
                  fill="rgba(0,180,216,.10)" stroke="var(--accent)" strokeWidth={2}
                  vectorEffect="non-scaling-stroke" pointerEvents="none" />
              );
            })()}
            <FlowNode n={n}
              resumen={n.kind === "inicio" ? resumen : null}
              seleccionado={st.sel.nodes.includes(id)}
              atenuado={!!resalte && !resalte.has(id)}
              editable={editable}
              minutos={minutosPorTarea[id]}
              puertoCaliente={st.drag?.mode === "link" && st.drag.from.nodeId === id
                ? st.drag.from.port : null}
              onPointerDown={nodoDown}
              onPointerDownPuerto={puertoDown}
              onDobleClic={onAbrirPaso} />
          </g>
        );
      })}
    </svg>
  );
}
